import { useEffect, useMemo, useRef, useState } from 'react';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import {
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useParams } from 'react-router-dom';

type AuthorizationSession = {
  id: string;
  clientId: string;
  clientName?: string;
  redirectUri: string;
  scope?: string;
};

type OAuthConsentPageProps = {
  onRedirect?: (url: string) => void;
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 760,
    margin: '0 auto',
  },
  card: {
    marginTop: theme.spacing(3),
  },
  warning: {
    marginBottom: theme.spacing(3),
  },
  metadata: {
    margin: 0,
  },
  term: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  value: {
    marginLeft: 0,
    overflowWrap: 'anywhere',
  },
  actions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
}));

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseSession(value: unknown): AuthorizationSession {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.clientId !== 'string' ||
    typeof value.redirectUri !== 'string' ||
    (value.clientName !== undefined && typeof value.clientName !== 'string') ||
    (value.scope !== undefined && typeof value.scope !== 'string')
  ) {
    throw new Error('The authorization server returned an invalid session.');
  }

  return {
    id: value.id,
    clientId: value.clientId,
    clientName: value.clientName,
    redirectUri: value.redirectUri,
    scope: value.scope,
  };
}

async function responseError(
  response: Response,
  fallback: string,
): Promise<Error> {
  let detail: string | undefined;

  try {
    const body: unknown = await response.json();
    if (isRecord(body)) {
      const candidate =
        body.error_description ?? body.message ?? body.error ?? body.details;
      if (typeof candidate === 'string') {
        detail = candidate;
      }
    }
  } catch {
    // The status below still gives the user an actionable error.
  }

  const status = [response.status, response.statusText]
    .filter(Boolean)
    .join(' ');
  return new Error(
    `${fallback}${detail || status ? `: ${detail ?? status}` : ''}`,
  );
}

function validRedirectUrl(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error('The authorization server did not return a redirect URL.');
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(
      'The authorization server returned an invalid redirect URL.',
    );
  }

  const scheme = url.protocol.slice(0, -1).toLowerCase();
  const isScriptScheme = scheme.startsWith('java') && scheme.endsWith('script');
  if (isScriptScheme || ['data', 'vbscript', 'file'].includes(scheme)) {
    throw new Error(
      'The authorization server returned an unsafe redirect URL.',
    );
  }

  return value;
}

export function OAuthConsentPage({
  onRedirect,
}: OAuthConsentPageProps): JSX.Element {
  const classes = useStyles();
  const { sessionId } = useParams<{ sessionId: string }>();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const submittingRef = useRef(false);
  const [session, setSession] = useState<AuthorizationSession>();
  const [loading, setLoading] = useState(Boolean(sessionId));
  const [loadError, setLoadError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>();
  const [handedOff, setHandedOff] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      const message = 'Authorization session ID is missing from the URL.';
      setLoadError(message);
      setLoading(false);
      alertApi.post({ message, severity: 'error' });
      return undefined;
    }

    let active = true;

    const loadSession = async () => {
      try {
        const authBaseUrl = await discoveryApi.getBaseUrl('auth');
        const response = await fetchApi.fetch(
          `${authBaseUrl.replace(/\/$/, '')}/v1/sessions/${encodeURIComponent(
            sessionId,
          )}`,
        );
        if (!response.ok) {
          throw await responseError(
            response,
            'Unable to load the authorization request',
          );
        }

        const loadedSession = parseSession(await response.json());
        if (loadedSession.id !== sessionId) {
          throw new Error(
            'The authorization server returned a different session.',
          );
        }

        if (active) {
          setSession(loadedSession);
        }
      } catch (error) {
        if (active) {
          const message =
            error instanceof Error
              ? error.message
              : 'Unable to load the authorization request.';
          setLoadError(message);
          alertApi.post({ message, severity: 'error' });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadSession();
    return () => {
      active = false;
    };
  }, [alertApi, discoveryApi, fetchApi, sessionId]);

  const clientMetadataUrl = useMemo(() => {
    if (!session) {
      return undefined;
    }
    try {
      return new URL(session.clientId);
    } catch {
      return undefined;
    }
  }, [session]);

  const clientLabel = session?.clientName || 'your application';

  const continueRef = useRef<HTMLAnchorElement>(null);
  // Move focus to the hand-off link once a decision is recorded, so finishing
  // the sign-in is a single keypress rather than a hunt for the new control.
  useEffect(() => {
    if (redirectUrl) {
      continueRef.current?.focus();
    }
  }, [redirectUrl]);

  const submit = async (decision: 'approve' | 'reject') => {
    if (submittingRef.current) {
      return;
    }
    if (!session) {
      const message = 'The authorization request is not available.';
      setSubmitError(message);
      alertApi.post({ message, severity: 'error' });
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(undefined);

    try {
      const authBaseUrl = await discoveryApi.getBaseUrl('auth');
      const response = await fetchApi.fetch(
        `${authBaseUrl.replace(/\/$/, '')}/v1/sessions/${encodeURIComponent(
          session.id,
        )}/${decision}`,
        { method: 'POST' },
      );
      if (!response.ok) {
        throw await responseError(
          response,
          `Unable to ${decision} the authorization request`,
        );
      }

      const result: unknown = await response.json();
      const target = validRedirectUrl(
        isRecord(result) ? result.redirectUrl : undefined,
      );
      // Keep the URL on screen. When the callback is an external protocol such
      // as vscode://, the browser hands off to the OS and leaves this document
      // in place, so an automatic redirect alone looks like a frozen page.
      setRedirectUrl(target);
      (onRedirect ?? (url => window.location.assign(url)))(target);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : `Unable to ${decision} the authorization request.`;
      setSubmitError(message);
      alertApi.post({ message, severity: 'error' });
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="Authorize application"
        subtitle="Review this OAuth access request"
      />
      <Content>
        <Grid className={classes.container}>
          {loading && <Progress />}
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {session && (
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h5" component="h1" gutterBottom>
                  {session.clientName || 'Unknown application'} is requesting
                  access
                </Typography>
                <Alert className={classes.warning} severity="warning">
                  Only approve this request if you trust this application and
                  initiated the sign-in. Approval allows it to act with the
                  scopes shown below.
                </Alert>
                {submitError && (
                  <Alert className={classes.warning} severity="error">
                    {submitError}
                  </Alert>
                )}
                {redirectUrl && (
                  <Alert
                    className={classes.warning}
                    severity={handedOff ? 'success' : 'info'}
                  >
                    <Typography paragraph>
                      {handedOff
                        ? `Sent to ${clientLabel}. Switch to it to finish signing in — this tab is safe to close.`
                        : `Your response has been recorded. Use the button below to hand off to ${clientLabel}. Browsers only launch a desktop application from a link you activate yourself, so this last step cannot be automated.`}
                    </Typography>
                    <Button
                      color="primary"
                      disableFocusRipple
                      href={redirectUrl}
                      onClick={() => setHandedOff(true)}
                      ref={continueRef}
                      variant="contained"
                    >
                      {handedOff
                        ? `Reopen ${clientLabel}`
                        : `Continue to ${clientLabel}`}
                    </Button>
                  </Alert>
                )}
                <dl className={classes.metadata}>
                  <Typography
                    className={classes.term}
                    component="dt"
                    variant="subtitle2"
                  >
                    Client name
                  </Typography>
                  <Typography className={classes.value} component="dd">
                    {session.clientName || 'Unknown application'}
                  </Typography>
                  <Typography
                    className={classes.term}
                    component="dt"
                    variant="subtitle2"
                  >
                    Client ID
                  </Typography>
                  <Typography className={classes.value} component="dd">
                    {session.clientId}
                  </Typography>
                  {clientMetadataUrl && (
                    <>
                      <Typography
                        className={classes.term}
                        component="dt"
                        variant="subtitle2"
                      >
                        Client metadata host
                      </Typography>
                      <Typography className={classes.value} component="dd">
                        {clientMetadataUrl.host || 'Not specified'}
                      </Typography>
                      <Typography
                        className={classes.term}
                        component="dt"
                        variant="subtitle2"
                      >
                        Client metadata URL
                      </Typography>
                      <Typography className={classes.value} component="dd">
                        {clientMetadataUrl.toString()}
                      </Typography>
                    </>
                  )}
                  <Typography
                    className={classes.term}
                    component="dt"
                    variant="subtitle2"
                  >
                    Callback URL
                  </Typography>
                  <Typography className={classes.value} component="dd">
                    {session.redirectUri}
                  </Typography>
                  <Typography
                    className={classes.term}
                    component="dt"
                    variant="subtitle2"
                  >
                    Requested scope
                  </Typography>
                  <Typography className={classes.value} component="dd">
                    {session.scope || 'No scopes specified'}
                  </Typography>
                </dl>
              </CardContent>
              <CardActions className={classes.actions}>
                {!redirectUrl && (
                  <>
                    <Button
                      disabled={submitting}
                      onClick={() => submit('reject')}
                      variant="outlined"
                    >
                      Reject
                    </Button>
                    <Button
                      color="primary"
                      disabled={submitting}
                      onClick={() => submit('approve')}
                      variant="contained"
                    >
                      Approve
                    </Button>
                  </>
                )}
              </CardActions>
            </Card>
          )}
        </Grid>
      </Content>
    </Page>
  );
}
